import { useState, useMemo, useCallback, useRef } from 'react';
import { Form } from '../../../../components';
import { handleError } from '../../../../lib/helpers';
import { useNotificationContext } from '../../../../providers';
import { useCloudAttributes } from '../hooks/useCloudAttributes';
import { useApplianceRedeployPrefill } from './useApplianceRedeployPrefill';
import { buildSourceCloudsPayload } from '../components/VMBasedMigrationsConfig/helpers';
import { useApplianceCommonState } from '../components/CommonApplianceConfiguration/useApplianceCommonState';
import { IP_TYPE, NIC_TYPE_ETHERNET } from '../../../../constants/network';

const NETWORK_FIELD_RESET = {
    networkName: undefined,
    subnet: undefined,
    assignPublicIp: 'none',
    networkTier: 'PREMIUM',
};

const buildGCPApplianceNetworkPayload = (values) => ({
    interfaces: {
        eth0: {
            ip_type: IP_TYPE.DHCP,
            type: NIC_TYPE_ETHERNET,
            network_name: values.networkName,
            internal_subnet_id: values.subnet,
            assign_public_ip: values.assignPublicIp === 'automatic',
            network_tier: values.networkTier || "STANDARD",
            dv_switch_name: '',
        },
    },
    tags: [],
});

export const useGCPApplianceSetup = ({ cloudAccount, onSubmit, isRedeploy }) => {
    const [form] = Form.useForm();
    const { notifyError } = useNotificationContext();

    const [projects, setProjects] = useState([]);
    const [regions, setRegions] = useState([]);
    const [zones, setZones] = useState([]);
    const [networks, setNetworks] = useState([]);
    const [instanceTags, setInstanceTags] = useState({});
    const [loadingProjectData, setLoadingProjectData] = useState(false);
    const [loadingRegionData, setLoadingRegionData] = useState(false);

    const projectAttributesCache = useRef({});
    const regionAttributesCache = useRef({});

    const project = Form.useWatch('project', form);
    const region = Form.useWatch('region', form);

    const {
        useProxyEnabled,
        migrationInstructions,
        ignoreValidationErrors,
        commonConfigProps,
        prefillCommonFromAppliance,
    } = useApplianceCommonState(form);

    const handleAttributesLoaded = (result) => {
        if (result?.properties?.projects) {
            setProjects(result.properties.projects);
        }
    };

    const { initialLoading, loadingError, loadCloudAttributes, fetchAttributes, loadingAttributes } =
        useCloudAttributes(cloudAccount, handleAttributesLoaded);

    const loadProjectRegions = useCallback(
        async (projectId) => {
            const cached = projectAttributesCache.current[projectId];
            if (cached) {
                setRegions(cached.regions || []);
                return cached;
            }
            setLoadingProjectData(true);
            try {
                const result = await fetchAttributes({
                    cloudAccountId: cloudAccount.id,
                    params: { project: projectId },
                }).unwrap();
                const projectData = result?.properties?.projects?.[0];
                if (projectData) {
                    projectAttributesCache.current[projectId] = projectData;
                    setRegions(projectData.regions || []);
                }
                return projectData;
            } catch (error) {
                handleError(error, notifyError, 'Failed to load project attributes');
                return null;
            } finally {
                setLoadingProjectData(false);
            }
        },
        [cloudAccount, fetchAttributes, notifyError],
    );

    const loadRegionNetworks = useCallback(
        async (projectId, regionName, regionFromList) => {
            if (regionFromList?.zones?.length) {
                setZones(regionFromList.zones);
            }
            const cacheKey = `${projectId}::${regionName}`;
            const cached = regionAttributesCache.current[cacheKey];
            if (cached) {
                setNetworks(cached.networks || []);
                if (cached.zones?.length) {
                    setZones(cached.zones);
                }
                return cached;
            }
            setLoadingRegionData(true);
            try {
                const result = await fetchAttributes({
                    cloudAccountId: cloudAccount.id,
                    params: { project: projectId, region: regionName },
                }).unwrap();
                const regionData =
                    result?.properties?.regions?.[0] || result?.properties?.projects?.[0]?.regions?.[0] || null;
                if (regionData) {
                    regionAttributesCache.current[cacheKey] = regionData;
                    setNetworks(regionData.networks || []);
                    if (regionData.zones?.length) {
                        setZones(regionData.zones);
                    }
                }
                return regionData;
            } catch (error) {
                handleError(error, notifyError, 'Failed to load region attributes');
                return null;
            } finally {
                setLoadingRegionData(false);
            }
        },
        [cloudAccount, fetchAttributes, notifyError],
    );

    useApplianceRedeployPrefill({
        isRedeploy,
        cloudAccount,
        projects,
        form,
        setInstanceTags,
        loadProjectRegions,
        loadRegionNetworks,
        prefillCommonFromAppliance,
    });

    const handleProjectChange = useCallback(
        (projectId) => {
            setRegions([]);
            setZones([]);
            setNetworks([]);
            form.setFieldsValue({ region: undefined, zone: undefined, ...NETWORK_FIELD_RESET });
            if (projectId) {
                loadProjectRegions(projectId);
            }
        },
        [form, loadProjectRegions],
    );

    const handleRegionChange = useCallback(
        (regionName) => {
            setZones([]);
            setNetworks([]);
            form.setFieldsValue({ zone: undefined, ...NETWORK_FIELD_RESET });
            if (project && regionName) {
                const regionFromList = regions.find((candidate) => candidate.name === regionName);
                loadRegionNetworks(project, regionName, regionFromList);
            }
        },
        [form, project, regions, loadRegionNetworks],
    );

    const handleFormSubmit = useCallback(
        (values) => {
            const cloudProperties = {
                project_id: values.project,
                region: values.region,
                az: values.zone,
                vapp_name: values.vmName?.trim() || undefined,
                network: buildGCPApplianceNetworkPayload(values),
                instance_type: values.machineType || 'e2-medium',
                disks_types: [values.diskType],
                tags: instanceTags || {},
                proxy_type: 'none',
            };

            const payload = {
                cloud_properties: cloudProperties,
                vm_level_migration_enabled: !!values.vmLevelEnabled,
                ignore_validation_errors: ignoreValidationErrors,
                ca_proxy: useProxyEnabled,
                migration_instructions: migrationInstructions,
            };

            if (values.vmLevelEnabled) {
                payload.source_clouds = buildSourceCloudsPayload(values);
            }

            onSubmit(payload);
        },
        [ignoreValidationErrors, useProxyEnabled, migrationInstructions, onSubmit, instanceTags],
    );

    const projectOptions = useMemo(() => {
        return projects.map((project) => (
            { value: project.identifier || project.name, label: project.name }
        ));
    },[projects]);

    const regionOptions = useMemo(() => {
        return regions.map((region) => (
            { value: region.name, label: region.name }
        ));
    }, [regions]);

    const zoneOptions = useMemo(() => {
        return zones.map((zone) => (
            { value: zone.name, label: zone.name }
        ));
    }, [zones]);

    return {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingAttributes,
        loadingProjectData,
        loadingRegionData,
        project,
        region,
        networks,
        instanceTags,
        setInstanceTags,
        handleProjectChange,
        handleRegionChange,
        handleFormSubmit,
        projectOptions,
        regionOptions,
        zoneOptions,
        commonConfigProps,
    };
};
